import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Experiment, Variation, useEnrolUser } from '@justgiving/experiments';
import Loader from '@justgiving/loading/es/Loading';
import { useTracking } from '@justgiving/react-tracking';
import classNames from 'classnames';

import { PageStatus } from '../../../types/__generated__';
import { Experiments } from '../../constants/experiments';
import pageTypes from '../../constants/pageTypes';
import { usePageMeta } from '../../hooks';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import isLightColor from '../../utils/colors/isLightColor';
import { PadlockIcon } from './PadlockIcon';
import { ShieldIcon } from './ShieldIcon';

import styles from './DonateButton.scss';

type DonateButtonProps = {
  className?: string;
  inactivePageBehaviour?: 'disable' | 'hide';
  onClick?: () => void;
  trackingDataPageSection?: string;
  experiments?: string[];
  successType?: string;
  label?: string;
};

export const DonateButton: FunctionComponent<DonateButtonProps> = ({
  className,
  inactivePageBehaviour = 'disable',
  onClick,
  trackingDataPageSection,
  experiments,
  successType,
  label,
}) => {
  const basePageData = useBasePageQuery();
  const { currentUrl, pageType } = usePageMeta();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('common', { useSuspense: false });
  const track = useTracking();

  const pageIsBranded = Boolean(
    basePageData.data?.page?.branding?.primaryColor,
  );
  const pageIsFundraisingOrCampaign = [
    pageTypes.FUNDRAISING,
    pageTypes.CAMPAIGN,
  ].includes(pageType);

  const { enrolledIn: B2C_036_Enrolment } = useEnrolUser(
    Experiments.b2c_036_donate_button_colour,
    !basePageData.data?.page || pageIsBranded || !pageIsFundraisingOrCampaign,
  );

  if (!basePageData.data?.page) {
    return null;
  }

  const { status, relationships, branding, lifecycle } = basePageData.data.page;
  //  lifecycle is a PageStatus equivalent for PS pages
  //  it contains an array with alowed actions that should drive the front end.
  const isDonateActive =
    status === PageStatus.Active ||
    (lifecycle?.allowedActions?.indexOf('public_donate') !== -1 &&
      lifecycle?.allowedActions?.indexOf('public_donate') !== undefined);

  const isMultiBeneficiaryCampaign =
    relationships && relationships.beneficiaries.totalCount > 1;

  if (inactivePageBehaviour === 'hide' && !isDonateActive) {
    return null;
  }

  const buttonType =
    branding?.primaryColor && isLightColor(branding.primaryColor)
      ? 'jg-btn--ghost'
      : 'jg-btn--themed';

  function handleClick() {
    setLoading(true);

    track({
      page_section: `${pageType} page > ${trackingDataPageSection ||
        'unknown'}`.toLowerCase(),
      event: 'link click',
      action_name: 'link click',
      event_value: 'donate',
      subtype: 'donate',
    });

    if (onClick) {
      onClick();
    }
  }

  const buildQueryString = () => {
    const trimmedSuccessType = successType?.trim();
    const trimmedExperiments = experiments
      ?.map(e => e.trim())
      .filter(e => e !== '');
    if (
      !trimmedExperiments ||
      trimmedExperiments.length === 0 ||
      !trimmedSuccessType
    ) {
      return '';
    }

    return `?experiments=${trimmedExperiments.join(
      ',',
    )}&successType=${trimmedSuccessType}`;
  };

  const getButtonLabel = () => {
    if (label) {
      return label;
    }

    if (pageType === pageTypes.FUNDRAISING) {
      return t('fundraising.donateLink');
    }

    return t('common.donateLink');
  };

  const donateUrl = `${currentUrl}/donate${buildQueryString()}`;

  return !isMultiBeneficiaryCampaign ? (
    <a
      type="button"
      className={classNames(
        'jg-btn',
        buttonType,
        styles.button,
        className,
        `jgp-b2c_036_${B2C_036_Enrolment}`,
        {
          [styles.disabled]: !isDonateActive,
        },
      )}
      onClick={handleClick}
      href={donateUrl}
    >
      {loading ? (
        <Loader size="small" colour="white" />
      ) : (
        <Experiment
          slug={Experiments.b2c_038_secure_donate_buttons}
          skipEnrolment={!pageIsFundraisingOrCampaign || label !== undefined}
        >
          <Variation name="A">
            <>{getButtonLabel()}</>
          </Variation>
          <Variation name="B">
            <div>
              <PadlockIcon className={styles.icon} />
              <span>{getButtonLabel()}</span>
            </div>
          </Variation>
          <Variation name="C">
            <div>
              <ShieldIcon className={styles.icon} />
              <span>{getButtonLabel()}</span>
            </div>
          </Variation>
          <Variation name="D">
            <>{t('common.donateLink.D')}</>
          </Variation>
        </Experiment>
      )}
    </a>
  ) : null;
};

export default DonateButton;
